import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLoginMutation } from '../features/auth/authApi';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { setCredentials } from '../features/auth/authSlice';
import InlineError from '../components/InlineError';
import LanguageToggle from '../components/LanguageToggle';
import noorhrLogo from '../assets/noorhr-logo.webp';

/** Auto-detect tenant slug from subdomain (production) */
function detectTenantSlug(): string {
  const hostname = window.location.hostname;

  // A raw IPv4 address (e.g. accessing the dev server from another device on
  // the LAN via "http://192.168.1.7:5173") also splits into 3+ dot-separated
  // parts, which would otherwise get misread as "<slug>.company.com" and
  // lock the workspace field to a bogus detected slug like "192" with no way
  // to edit it. Same story for "localhost"/plain hostnames with no dots.
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
    return '';
  }

  // e.g. "acme.hrms.com" → "acme"
  // Ignore: localhost, hrms.com, www.hrms.com
  const parts = hostname.split('.');

  // Exclude common deployment prefixes
  const ignoredPrefixes = ['www', 'app', 'testing', 'staging', 'development', 'demo'];

  if (parts.length >= 3 && !ignoredPrefixes.includes(parts[0])) {
    return parts[0];
  }
  return '';
}

// The brand blue picked out of the noorHR wordmark — used to tie small
// accents (the "your workspace" subtitle, chip text) back to the logo now
// that the page is light instead of relying on translucent-white-on-navy.
const BRAND_BLUE = '#2563eb';

export default function Login() {
  const { t } = useTranslation();
  const [loginMethod, setLoginMethod] = useState<'email' | 'employeeId'>('email');
  const [email, setEmail] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [tenantSlug, setTenantSlug] = useState('');
  const [subdomainDetected, setSubdomainDetected] = useState(false);
  const [login, { isLoading, error }] = useLoginMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Only use `from` if the user was actually redirected from a protected page.
  // Do NOT default to /dashboard — role-based redirect handles the default.
  const from = (location.state as any)?.from?.pathname || '';

  useEffect(() => {
    // 1. Priority: State from navigation (e.g. after password reset)
    if (location.state?.tenant_slug) {
      setTenantSlug(location.state.tenant_slug);
    }
    // 2. Secondary: Subdomain detection
    else {
      const detected = detectTenantSlug();
      if (detected) {
        setTenantSlug(detected);
        setSubdomainDetected(true);
      }
    }

    // 3. Populate email if provided in state
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const result = await login(
        loginMethod === 'employeeId'
          ? { employeeId, password, tenant_slug: tenantSlug }
          : { email, password, tenant_slug: tenantSlug }
      ).unwrap();
      if (result.verification_required) {
        navigate('/verify-otp', {
          state: { email: result.email, tenant_slug: result.tenant_slug, from },
        });
        return;
      }
      // Direct login (no OTP) — set credentials and redirect by role
      dispatch(setCredentials({ accessToken: result.accessToken!, user: result.user! }));
      const role = result.user?.role || 'HR_ADMIN';
      const isEmployee = role.toLowerCase() === 'employee';
      const defaultHome = isEmployee ? '/ess/dashboard' : '/dashboard';

      let destination = defaultHome;
      if (from) {
          if (isEmployee && !from.startsWith('/ess')) {
              destination = defaultHome;
          } else {
              destination = from;
          }
      }

      navigate(destination, { replace: true });
    } catch (err: any) {
      // handled by InlineError
    }
  }

  return (
    <div
      className="flex min-h-screen relative"
      style={{ fontFamily: "'Inter', sans-serif", background: 'linear-gradient(135deg, #bfdbfe 0%, #93c5fd 50%, #60a5fa 100%)' }}
    >
      <div className="absolute top-6 right-6 rtl:right-auto rtl:left-6 z-20">
        <LanguageToggle />
      </div>

      {/* Left panel — branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #3b82f6, transparent)', transform: 'translate(30%, -30%)' }} />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #6366f1, transparent)', transform: 'translate(-30%, 30%)' }} />

        {/* Logo — original colors, no wrapper needed on a white panel. */}
        <div className="relative z-10">
          <img src={noorhrLogo} alt="noorHR" className="h-14 w-auto" />
        </div>

        {/* Hero text */}
        <div className="relative z-10">
          <h2 className="text-4xl font-bold text-gray-900 leading-tight mb-4">
            {t('login.heroTitleLine1')}<br />
            <span style={{ color: BRAND_BLUE }}>
              {t('login.heroTitleLine2')}
            </span>
          </h2>
          <p className="text-gray-500 text-lg leading-relaxed">
            {t('login.heroSubtitle')}
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap gap-3 mt-8">
            {[t('login.pills.multiTenant'), t('login.pills.roleBasedAccess'), t('login.pills.essPortal'), t('login.pills.payroll')].map((f) => (
              <span key={f} className="px-3 py-1 rounded-full text-sm font-medium" style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)', color: BRAND_BLUE }}>
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom quote */}
        <div className="relative z-10">
          <p className="text-gray-400 text-sm italic">{t('login.quote')}</p>
        </div>
      </div>

      {/* Right panel — login form. Dark navy, independent of the left
          panel's light gradient - an explicit background here overrides
          whatever the shared wrapper would otherwise show through. */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12" style={{ background: '#0f172a' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo — white silhouette again now that this panel is dark. */}
          <div className="lg:hidden mb-8">
            <img src={noorhrLogo} alt="noorHR" className="h-12 w-auto brightness-0 invert" />
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white">{t('login.welcomeBack')}</h1>
            <p className="mt-2 text-blue-300">{t('login.signInSubtitle')}</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5">
            {/* Company Domain */}
            {!subdomainDetected ? (
              <div>
                <label htmlFor="tenantSlug" className="block text-sm font-medium text-blue-200 mb-2">
                  {t('login.companyDomain')}
                </label>
                <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
                  <input
                    id="tenantSlug"
                    type="text"
                    value={tenantSlug}
                    onChange={(e) => setTenantSlug(e.target.value.toLowerCase().replace(/\s/g, ''))}
                    className="flex-1 px-4 py-3 text-white placeholder-blue-400 outline-none border-0"
                    style={{ background: 'rgba(255,255,255,0.05)' }}
                    placeholder="acme"
                    required
                    autoComplete="organization"
                  />
                  <span className="flex items-center px-4 text-sm text-blue-400 whitespace-nowrap" style={{ background: 'rgba(255,255,255,0.03)', borderLeft: '1px solid rgba(255,255,255,0.1)' }}>
                    .hrms.com
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
                <svg className="w-5 h-5 text-blue-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <div>
                  <p className="text-xs text-blue-400">{t('login.workspace')}</p>
                  <p className="text-white font-semibold">{tenantSlug}.hrms.com</p>
                </div>
              </div>
            )}

            {/* Login method toggle */}
            <div className="flex rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                type="button"
                onClick={() => setLoginMethod('email')}
                className="flex-1 py-2 text-sm font-medium transition-colors"
                style={{
                  background: loginMethod === 'email' ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: loginMethod === 'email' ? '#fff' : '#93c5fd',
                }}
              >
                {t('login.emailTab')}
              </button>
              <button
                type="button"
                onClick={() => setLoginMethod('employeeId')}
                className="flex-1 py-2 text-sm font-medium transition-colors"
                style={{
                  background: loginMethod === 'employeeId' ? 'rgba(59,130,246,0.2)' : 'transparent',
                  color: loginMethod === 'employeeId' ? '#fff' : '#93c5fd',
                }}
              >
                {t('login.employeeIdTab')}
              </button>
            </div>

            {/* Email or Employee ID */}
            {loginMethod === 'email' ? (
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-2">
                  {t('login.emailLabel')}
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="you@company.com"
                  required
                  autoComplete="email"
                />
              </div>
            ) : (
              <div>
                <label htmlFor="employeeId" className="block text-sm font-medium text-blue-200 mb-2">
                  {t('login.employeeIdLabel')}
                </label>
                <input
                  id="employeeId"
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="EMP-1712"
                  required
                  autoComplete="username"
                />
              </div>
            )}

            {/* Password */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="password" className="block text-sm font-medium text-blue-200">
                  {t('login.passwordLabel')}
                </label>
                <Link to="/forgot-password" className="text-xs text-blue-400 hover:text-white transition-colors">
                  {t('login.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 rtl:pr-4 rtl:pl-12 rounded-xl text-white placeholder-blue-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 rtl:right-auto rtl:left-3 top-1/2 -translate-y-1/2 text-blue-400 hover:text-white transition-colors"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <InlineError message={(error as any)?.data?.error?.message || (error as any)?.data?.message || (error as any)?.error} />

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 rounded-xl font-semibold text-white text-base transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-90 active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {t('login.signingIn')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {t('login.signIn')}
                  <svg className="w-4 h-4 rtl:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-blue-400 text-sm">
              {t('login.newCompany')}{' '}
              <Link to="/register" className="text-blue-300 hover:text-white font-medium transition-colors">
                {t('login.registerWorkspace')}
              </Link>
            </p>
          </div>

          {/* Divider */}
          <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <p className="text-center text-xs text-blue-500">
              {t('login.securedFooter')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
